import { useQueries, useQuery } from '@tanstack/react-query';

import { Endpoints } from '@/api';
import { useFetch } from '@/utils/fetch';

import { RequestWithRelations } from './request';

export type ProductAttribute =
  Endpoints['GET /product-attributes']['response']['productAttributes'][number];

export type ProductAttributeValueWithBrand = ProductAttribute['productAttributeValues'][number];
export type ProductAttributeValue = Omit<ProductAttributeValueWithBrand, 'brands'>;

export type ProductAttributesByArticle = {
  articleId: string;
  brandId: string | null;
  productAttributes: ProductAttribute[];
};

const ROOT_PARENT_KEY = '__root__';

type ProductAttributeValueNode<TProductAttributeValue> = {
  value: TProductAttributeValue;
  children: ProductAttributeValueNode<TProductAttributeValue>[];
};

const getProductAttributesByParentValueId = ({
  productAttributes,
}: {
  productAttributes: ProductAttribute[];
}) => {
  return productAttributes.reduce<Record<string, ProductAttribute[]>>((acc, attribute) => {
    const key = attribute.parentProductAttributeValueId ?? ROOT_PARENT_KEY;

    acc[key] = [...(acc[key] ?? []), attribute];

    return acc;
  }, {});
};

const getProductAttributeValuesByParentValueId = <
  TProductAttributeValue extends {
    productAttribute: { parentProductAttributeValueId: string | null };
  },
>({
  productAttributeValues,
}: {
  productAttributeValues: TProductAttributeValue[];
}) => {
  return productAttributeValues.reduce<Record<string, TProductAttributeValue[]>>((acc, value) => {
    const key = value.productAttribute.parentProductAttributeValueId ?? ROOT_PARENT_KEY;

    acc[key] = [...(acc[key] ?? []), value];

    return acc;
  }, {});
};

export const getRootProductAttributes = ({
  productAttributes,
}: {
  productAttributes: ProductAttribute[];
}) => {
  const attributesByParentValueId = getProductAttributesByParentValueId({ productAttributes });

  return attributesByParentValueId[ROOT_PARENT_KEY] ?? [];
};

export const getChildProductAttributesByParentValueId = ({
  productAttributes,
}: {
  productAttributes: ProductAttribute[];
}): Record<string, ProductAttribute[]> => {
  const attributesByParentValueId = getProductAttributesByParentValueId({ productAttributes });

  return Object.entries(attributesByParentValueId).reduce<Record<string, ProductAttribute[]>>(
    (acc, [key, attributes]) => {
      if (key !== ROOT_PARENT_KEY) {
        acc[key] = attributes;
      }

      return acc;
    },
    {}
  );
};

export const getSelectableProductAttributeValues = ({
  productAttributeValues,
  selectedValueIds,
}: {
  productAttributeValues: ProductAttributeValue[];
  selectedValueIds: string[];
}) => {
  return productAttributeValues.filter(
    (productAttributeValue) =>
      productAttributeValue.isSelectable || selectedValueIds.includes(productAttributeValue.id)
  );
};

export const getSelectedProductAttributeValueIdsWithParents = ({
  productAttributes,
  selectedValueIds,
}: {
  productAttributes: ProductAttribute[];
  selectedValueIds: string[];
}) => {
  const selectedValueIdsSet = new Set(selectedValueIds);
  const parentValueIdByValueId = productAttributes.reduce<Record<string, string | null>>(
    (acc, productAttribute) => {
      for (const productAttributeValue of productAttribute.productAttributeValues) {
        acc[productAttributeValue.id] = productAttribute.parentProductAttributeValueId;
      }

      return acc;
    },
    {}
  );

  const hasAllParentsSelected = (valueId: string): boolean => {
    let currentParentValueId = parentValueIdByValueId[valueId] ?? null;
    const visitedValueIds = new Set<string>();

    while (currentParentValueId) {
      if (visitedValueIds.has(currentParentValueId)) {
        return false;
      }

      visitedValueIds.add(currentParentValueId);

      if (!selectedValueIdsSet.has(currentParentValueId)) {
        return false;
      }

      currentParentValueId = parentValueIdByValueId[currentParentValueId] ?? null;
    }

    return true;
  };

  return selectedValueIds.filter((valueId) => hasAllParentsSelected(valueId));
};

export const getSelectedProductAttributeValueIdsWithAutoSelectedSingleChildren = ({
  productAttributes,
  selectedValueIds,
}: {
  productAttributes: ProductAttribute[];
  selectedValueIds: string[];
}) => {
  const selectedValueIdsSet = new Set(
    getSelectedProductAttributeValueIdsWithParents({
      productAttributes,
      selectedValueIds,
    })
  );

  let hasAddedValue = true;

  while (hasAddedValue) {
    hasAddedValue = false;

    for (const selectedValueId of selectedValueIdsSet) {
      const childAttributes = productAttributes.filter(
        (productAttribute) => productAttribute.parentProductAttributeValueId === selectedValueId
      );

      for (const childAttribute of childAttributes) {
        const selectableChildValues = getSelectableProductAttributeValues({
          productAttributeValues: childAttribute.productAttributeValues,
          selectedValueIds: [...selectedValueIdsSet],
        });

        if (selectableChildValues.length !== 1) {
          continue;
        }

        const [onlySelectableChildValue] = selectableChildValues;

        if (selectedValueIdsSet.has(onlySelectableChildValue.id)) {
          continue;
        }

        selectedValueIdsSet.add(onlySelectableChildValue.id);
        hasAddedValue = true;
      }
    }
  }

  return getSelectedProductAttributeValueIdsWithParents({
    productAttributes,
    selectedValueIds: [...selectedValueIdsSet],
  });
};

export const getSelectedProductAttributeValuesTree = <
  TProductAttributeValue extends {
    id: string;
    productAttribute: { parentProductAttributeValueId: string | null };
  },
>({
  productAttributeValues,
}: {
  productAttributeValues: TProductAttributeValue[];
}): ProductAttributeValueNode<TProductAttributeValue>[] => {
  const valuesByParentValueId = getProductAttributeValuesByParentValueId({
    productAttributeValues,
  });

  const getChildren = (
    parentValueId: string | null
  ): ProductAttributeValueNode<TProductAttributeValue>[] => {
    const key = parentValueId ?? ROOT_PARENT_KEY;
    const currentValues = valuesByParentValueId[key] ?? [];

    return currentValues.map((value) => ({
      value,
      children: getChildren(value.id),
    }));
  };

  return getChildren(null);
};

export const useProductAttributes = ({
  organizationId,
  brandIds,
}: {
  organizationId: string | undefined;
  brandIds?: string[];
}) => {
  const fetch = useFetch<Endpoints['GET /product-attributes']>();

  return useQuery({
    queryKey: ['product-attributes', organizationId, brandIds],
    queryFn: () => fetch('/product-attributes', { organizationId: organizationId!, brandIds }),
    enabled: !!organizationId,
  });
};

const useProductAttributesQueries = (request: RequestWithRelations | undefined) => {
  const fetch = useFetch<Endpoints['GET /product-attributes']>();
  const organizationId = request?.organizationId;

  return useQueries({
    queries: (request?.articles ?? []).map((article) => ({
      queryKey: ['product-attributes', organizationId, article.brandId],
      queryFn: () =>
        fetch('/product-attributes', {
          organizationId: organizationId!,
          brandIds: article.brandId ? [article.brandId] : [],
        }),
      enabled: !!organizationId,
    })),
  });
};

export const useProductAttributesByArticle = (request: RequestWithRelations | undefined) => {
  const productAttributesQueries = useProductAttributesQueries(request);

  return request?.articles.map((article, index) => ({
    articleId: article.id,
    brandId: article.brandId,
    productAttributes: productAttributesQueries[index]?.data?.productAttributes ?? [],
  }));
};
